import { z } from 'zod';
import { create } from 'zustand';

import { focusHoursSchema } from '../lib/organization/focusHours';

/**
 * The company record and its members, for the admin screens (PLAN-GENERICITE.md § 3.2, § 4 quater
 * step 2). The single front source of `organization_profile()`: the same SQL reader as the Edge
 * Functions (`supabase/functions/_shared/organizationIdentity.ts`), read by `useOrganization()`.
 * Read by Settings > Company and team; its writes go through `src/services/organizationService.ts`,
 * which reads the record again into this store after each one.
 *
 * The schema checks the shape, field for field the Edge one. The values were judged by the database
 * constraints, and what the database derives (`internal` = `is_internal_email(email)`, the owner
 * first) is read, never re-derived here (principle 11). Unknown keys are dropped, so a column added
 * later breaks nothing.
 */

const text = z.string();
const nullableText = z.string().nullable();
const textList = z.array(text).readonly();

export const organizationMemberSchema = z
  .object({
    user_id: z.string().uuid(),
    display_name: nullableText,
    first_name: nullableText,
    email: nullableText,
    /** False for a service or test account at an outside address. */
    internal: z.boolean(),
    role: z.enum(['owner', 'admin', 'member']),
    /** Data domains (`data_domains.slug`) the member reaches; empty = all. Stored, not enforced yet. */
    scope: textList,
    job_title: nullableText,
    meeting_names: textList,
    can_open_autonomy: z.boolean(),
    /** The member's focus hours; null (or a server from before 09/10/2026): the company's. */
    focus_hours: focusHoursSchema.nullable().default(null),
  })
  .readonly();

export const organizationProfileSchema = z
  .object({
    id: z.string().uuid(),
    name: text,
    short_name: text,
    activity: text,
    clientele: nullableText,
    /** The description (D16): the sector's slug and label, the detailed text, the trade's words, the ecosystem. Null before 10/10/2026. */
    sector: nullableText.default(null),
    sector_label: nullableText.default(null),
    description: nullableText.default(null),
    vocabulary: nullableText.default(null),
    ecosystem: nullableText.default(null),
    internal_domains: textList,
    timezone: text,
    locale: text,
    website_url: nullableText,
    /** The website's bare domain, `website_domain(website_url)`, derived by the database (a generated column). */
    website_domain: nullableText,
    address: nullableText,
    phone: nullableText,
    logo_url: nullableText,
    /** By purpose: `{"<purpose>": {"email": "…", "name": "…"}}`, `name` optional. */
    senders: z.record(z.string(), z.object({ email: text, name: text.optional() }).readonly()).readonly(),
    /** By purpose: `{"<purpose>": "<host>"}`. */
    app_hosts: z.record(z.string(), text).readonly(),
    created_at: text,
    updated_at: text,
    members: z.array(organizationMemberSchema).readonly(),
  })
  .readonly();

export type OrganizationProfile = z.infer<typeof organizationProfileSchema>;
export type OrganizationMember = OrganizationProfile['members'][number];
export type OrganizationRole = OrganizationMember['role'];

/** The answer of `organization_profile()`: a profile, null (no organization yet), or a drifted shape. */
export type ParsedOrganizationProfile =
  | { readonly ok: true; readonly profile: OrganizationProfile | null }
  | { readonly ok: false; readonly issues: readonly string[] };

/** Pure. `issues` are `path: message`, for the log, never for the screen. */
export function parseOrganizationProfile(raw: unknown): ParsedOrganizationProfile {
  if (raw === null || raw === undefined) return { ok: true, profile: null };
  const parsed = organizationProfileSchema.safeParse(raw);
  if (!parsed.success) {
    return { ok: false, issues: parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`) };
  }
  return { ok: true, profile: parsed.data };
}

/**
 * `idle`: never asked. `empty`: no organization yet (the seed has not run) — a state to explain,
 * not an error. `error.message` is for the screen, in French.
 */
export type OrganizationState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly organization: OrganizationProfile }
  | { readonly status: 'empty' }
  | { readonly status: 'error'; readonly message: string };

interface OrganizationStore {
  readonly state: OrganizationState;
  /** A read starts. What is on screen (a record, or its absence) stays until the answer replaces it. */
  readonly startLoading: () => void;
  /** The read answered: the record, or null when there is no organization yet. */
  readonly setProfile: (profile: OrganizationProfile | null) => void;
  readonly setError: (message: string) => void;
}

const LOADING: OrganizationState = { status: 'loading' };

/** State and pure setters only; the read lives in `useOrganization` (ENGINEERING.md § 5). */
export const useOrganizationStore = create<OrganizationStore>()((set) => ({
  state: { status: 'idle' },
  startLoading: () =>
    set((s) => (s.state.status === 'ready' || s.state.status === 'empty' ? s : { state: LOADING })),
  setProfile: (profile) =>
    set({ state: profile ? { status: 'ready', organization: profile } : { status: 'empty' } }),
  setError: (message) => set({ state: { status: 'error', message } }),
}));
